<template>
  <div class="api-integrations">
    <div class="api-integrations-container q-pa-md">
      <div v-if="loading" class="flex flex-center q-py-xl">
        <q-spinner color="primary" size="3em" />
      </div>

      <!-- 403 forbidden access -->
      <div v-else-if="forbidden" class="forbidden-container text-center q-py-xl q-px-md">
        <q-icon name="gpp_bad" size="4.5rem" color="negative" class="q-mb-md" />
        <h3 class="text-h5 text-weight-bold text-black q-mb-md">{{ $t('userPanel.apiIntegrations.error.forbidden') }}</h3>
        <p class="text-body1 text-grey-8 max-width-600 q-mx-auto">
          {{ $t('userPanel.apiIntegrations.forbiddenHelp') }}
        </p>
      </div>

      <!-- General Error message -->
      <div v-else-if="error" class="error-container text-center q-py-xl">
        <q-icon name="error_outline" size="4em" color="negative" class="q-mb-sm" />
        <div class="text-h6 text-black">{{ error }}</div>
        <q-btn :label="$t('userPanel.apiIntegrations.retry')" color="primary" class="q-mt-md" @click="fetchIntegrations" />
      </div>

      <div v-else>
        <header class="flex justify-between q-pb-md">
          <h2 class="text-h4 q-my-sm text-black">{{ $t('userPanel.apiIntegrations.title') }}</h2>
          <q-btn
            color="primary"
            icon="add"
            :label="$t('userPanel.apiIntegrations.newIntegration')"
            class="header-button"
            @click="openCreateDialog"
          />
        </header>

        <div class="text-subtitle2 text-grey-7 q-mb-md">
          {{ $t('userPanel.apiIntegrations.subtitle') }}
          <span class="q-ml-xs">
            {{ $t('userPanel.apiIntegrations.docsInfo') }}
            <a
              href="#"
              class="text-primary text-weight-bold docs-link"
              @click.prevent="openDocs"
            >
              {{ $t('userPanel.apiIntegrations.docsLink') }}
              <q-icon v-if="!loadingDocs" name="open_in_new" size="14px" />
              <q-spinner v-else size="14px" class="q-ml-xs" />
            </a>.
          </span>
        </div>

        <div v-if="integrations.length === 0" class="empty-state text-center q-py-xl q-my-lg">
          <q-icon name="cable" size="4.5rem" color="grey-6" class="q-mb-md" />
          <div class="text-h6 text-grey-8">{{ $t('userPanel.apiIntegrations.emptyStateTitle') }}</div>
          <div class="text-body2 text-grey-7 q-mt-sm">
            {{ $t('userPanel.apiIntegrations.emptyStateSub') }}
          </div>
        </div>

        <div v-else class="api-integrations-list q-gutter-md">
          <ApiIntegrationCard
            v-for="integration in integrations"
            :key="integration.id"
            :integration="integration"
            @rotate="confirmRotate"
            @deactivate="confirmDeactivate"
            @reactivate="confirmReactivate"
          />
        </div>
      </div>

      <ApiIntegrationFormDialog
        v-model="createDialog.show"
        :submitting="createDialog.submitting"
        @submit="submitCreate"
      />

      <ApiIntegrationKeyRevealDialog
        v-model="revealDialog.show"
        :plainKey="revealDialog.plainKey"
        @close="closeRevealDialog"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { notifySuccess, notifyError } from "@/utils/notify";
import ApiIntegrationCard from "./ApiIntegrationCard.vue";
import ApiIntegrationFormDialog from "./ApiIntegrationFormDialog.vue";
import ApiIntegrationKeyRevealDialog from "./ApiIntegrationKeyRevealDialog.vue";

export default {
  name: "ApiIntegrationsWrapper",
  components: {
    ApiIntegrationCard,
    ApiIntegrationFormDialog,
    ApiIntegrationKeyRevealDialog
  },
  data() {
    return {
      loading: true,
      forbidden: false,
      error: null,
      createDialog: {
        show: false,
        submitting: false
      },
      revealDialog: {
        show: false,
        plainKey: ""
      },
      loadingDocs: false
    };
  },
  computed: {
    ...mapState("apiIntegrations", {
      integrations: (state) => state.integrationsList
    })
  },
  mounted() {
    this.fetchIntegrations();
  },
  methods: {
    ...mapActions("apiIntegrations", {
      fetchIntegrationsAction: "fetchIntegrations",
      createIntegrationAction: "createIntegration",
      rotateIntegrationKeyAction: "rotateIntegrationKey",
      deactivateIntegrationAction: "deactivateIntegration",
      reactivateIntegrationAction: "reactivateIntegration",
      requestDocsAccessAction: "requestDocsAccess"
    }),
    async fetchIntegrations() {
      this.loading = true;
      this.forbidden = false;
      this.error = null;
      try {
        await this.fetchIntegrationsAction();
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.forbidden = true;
        } else {
          this.error = this.$t(this.getErrorMessageKey(error));
        }
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.createDialog.show = true;
    },
    async submitCreate(form) {
      this.createDialog.submitting = true;
      try {
        let normalizedDomain = form.domain.trim();
        normalizedDomain = normalizedDomain.replace(/^(https?:\/\/)?(www\.)?/, "");
        normalizedDomain = normalizedDomain.split("/")[0].split(":")[0];

        const payload = {
          ...form,
          domain: normalizedDomain
        };

        const data = await this.createIntegrationAction(payload);
        if (data && data.message === "success") {
          this.createDialog.show = false;

          this.revealDialog.plainKey = data.data.plain_api_key;
          this.revealDialog.show = true;
        }
      } catch (error) {
        const messageKey = this.getErrorMessageKey(error);
        notifyError(messageKey);
      } finally {
        this.createDialog.submitting = false;
      }
    },
    getErrorMessageKey(error) {
      const errMsgKey = error.response?.data?.message;
      if (errMsgKey && this.$t(errMsgKey) !== errMsgKey) {
        return errMsgKey;
      }

      return 'userPanel.apiIntegrations.error.unknown';
    },
    closeRevealDialog() {
      this.revealDialog.show = false;
      this.revealDialog.plainKey = "";
      this.fetchIntegrations();
    },
    confirmRotate(integration) {
      this.$q.dialog({
        title: this.$t('userPanel.apiIntegrations.confirm.rotateTitle'),
        message: this.$t('userPanel.apiIntegrations.confirm.rotateMessage'),
        persistent: true,
        ok: {
          label: this.$t('userPanel.apiIntegrations.actions.rotate'),
          color: 'warning',
          flat: true
        },
        cancel: {
          label: this.$t('userPanel.apiIntegrations.cancel'),
          color: 'grey-4',
          flat: true
        }
      }).onOk(async () => {
        try {
          const data = await this.rotateIntegrationKeyAction(integration.id);
          if (data && data.message === "success") {
            this.revealDialog.plainKey = data.data.plain_api_key;
            this.revealDialog.show = true;
          }
        } catch (error) {
          const messageKey = this.getErrorMessageKey(error);
          notifyError(messageKey);
        }
      });
    },
    confirmDeactivate(integration) {
      this.$q.dialog({
        title: this.$t('userPanel.apiIntegrations.confirm.deactivateTitle'),
        message: this.$t('userPanel.apiIntegrations.confirm.deactivateMessage'),
        html: true,
        persistent: true,
        ok: {
          label: this.$t('userPanel.apiIntegrations.actions.deactivate'),
          color: 'negative',
          flat: true
        },
        cancel: {
          label: this.$t('userPanel.apiIntegrations.cancel'),
          color: 'grey-4',
          flat: true
        }
      }).onOk(async () => {
        try {
          const data = await this.deactivateIntegrationAction(integration.id);
          if (data && data.message === "success") {
            notifySuccess('userPanel.apiIntegrations.success.deactivated');
            this.fetchIntegrations();
          }
        } catch (error) {
          const messageKey = this.getErrorMessageKey(error);
          notifyError(messageKey);
        }
      });
    },
    confirmReactivate(integration) {
      this.$q.dialog({
        title: this.$t('userPanel.apiIntegrations.confirm.reactivateTitle'),
        message: this.$t('userPanel.apiIntegrations.confirm.reactivateMessage'),
        persistent: true,
        ok: {
          label: this.$t('userPanel.apiIntegrations.actions.reactivate'),
          color: 'positive',
          flat: true
        },
        cancel: {
          label: this.$t('userPanel.apiIntegrations.cancel'),
          color: 'grey-4',
          flat: true
        }
      }).onOk(async () => {
        try {
          const data = await this.reactivateIntegrationAction(integration.id);
          if (data && data.message === "success") {
            this.revealDialog.plainKey = data.data.plain_api_key;
            this.revealDialog.show = true;
          }
        } catch (error) {
          const messageKey = this.getErrorMessageKey(error);
          notifyError(messageKey);
        }
      });
    },
    async openDocs() {
      if (this.loadingDocs) {
        return;
      }
      this.loadingDocs = true;
      try {
        const docsUrl = await this.requestDocsAccessAction();
        window.open(docsUrl, "_blank");
      } catch (error) {
        const messageKey = this.getErrorMessageKey(error);
        notifyError(messageKey);
      } finally {
        this.loadingDocs = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.api-integrations {
  background: #fff;
  width: 100%;
  min-height: calc(100vh - 50px);
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding-bottom: 50px;

  &-container {
    width: 100%;
    max-width: 1000px;

    header {
      gap: 10px;
    }

    .header-button {
      margin: 10px 0;
      margin-left: auto;
    }
  }

  &-list {
    display: flex;
    flex-direction: column;
  }
}

.forbidden-container, .error-container, .empty-state {
  background: #f9f9f9;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  border-radius: 12px;
}

.forbidden-container {
  border-color: rgba($negative, 0.3);
  background: rgba($negative, 0.02);
}

.max-width-600 {
  max-width: 600px;
}

.docs-link {
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
